/**
 * TagColorPicker Component
 *
 * Uses a simple timeout for focus management to handle the Popover component's
 * own focus management when closing after color selection.
 */
import React, { useState, useRef } from 'react';
import { Col, Row } from 'antd';
import { Popover } from 'syngenta-digital-cropwise-react-ui-kit';
import ArrowDropDownIcon from 'components/Icons/ArrowDropDownIcon';
import TagColorPickerContent from '../TagColorPickerContent';
import { TAG_MANAGER_STRINGS } from '../../constants';
import { isSelectKey } from '../../helpers/keyboardUtils';

interface TagColorPickerProps {
  tagId: string;
  value: string;
  onChange: (tagId: string, color: string) => void;
}

const TagColorPicker: React.FC<TagColorPickerProps> = ({
  tagId,
  value,
  onChange
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasClosedBySelection = useRef(false);

  const onColorSelect = (color: string) => {
    onChange(tagId, color);
    wasClosedBySelection.current = true;
    setOpen(false);

    // Return focus to the trigger button after color selection
    // Small timeout needed to handle Popover's focus management
    setTimeout(() => {
      if (triggerRef.current) {
        triggerRef.current.focus({ preventScroll: true });
      }
    }, 50);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);

    if (!newOpen) {
      wasClosedBySelection.current = false;
    }
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  return (
    <Popover
      content={
        <TagColorPickerContent value={value} onColorSelect={onColorSelect} />
      }
      trigger={['click']}
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpenChange}
      destroyTooltipOnHide
    >
      <button
        type="button"
        ref={triggerRef}
        data-testid={TAG_MANAGER_STRINGS.TEST_ID_COLOR_PICKER}
        className={`rounded cursor-pointer flex items-center border-0 ${
          open ? 'bg-blue-10 hover:bg-blue-10' : 'hover:bg-neutral-100'
        }`}
        style={{
          height: 40,
          transition: 'all 0.2s ease-in-out'
        }}
        aria-label={TAG_MANAGER_STRINGS.SELECT_TAG_COLOR}
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={e => {
          if (isSelectKey(e.key)) {
            e.preventDefault();
            e.stopPropagation();
            handleToggle();
          }
        }}
        onClick={e => {
          e.stopPropagation();
          handleToggle();
        }}
      >
        <Row gutter={8} className="items-center">
          <Col>
            <div
              className={`rounded cursor-pointer ${
                open ? 'border-2 border-blue-50' : ''
              }`}
              style={{
                background: value,
                height: '40px',
                width: '40px'
              }}
            />
          </Col>
          <Col>
            <ArrowDropDownIcon color={open ? '#0092E4' : undefined} />
          </Col>
        </Row>
      </button>
    </Popover>
  );
};

export default TagColorPicker;
